/** @format */

import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import * as AuthActions from "../../../store/action/AuthAction";
import Alert from "components/Alert";

// Same override WaiterLogin already uses, kept identical so the two forms
// look like one system rather than two different logins bolted together.
const theme = createTheme({
   components: {
      MuiTextField: {
         styleOverrides: {
            root: {
               "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                     borderColor: "#393D3D",
                     borderRadius: "10px",
                  },
                  "&:hover fieldset": {
                     borderColor: "#393D3D",
                  },
                  "&.Mui-focused fieldset": {
                     borderColor: "#000000",
                  },
               },
            },
         },
      },
   },
});

const loginSchema = yup.object().shape({
   username: yup.string().required("Username is required"),
   password: yup.string().required("Password is required"),
});

const forgotRequestSchema = yup.object().shape({
   username: yup.string().required("Username is required"),
});

const forgotVerifySchema = yup.object().shape({
   otp: yup.string().required("Enter the code you were sent"),
   new_password: yup.string().min(8, "At least 8 characters").required("Enter a new password"),
   confirm_password: yup
      .string()
      .oneOf([yup.ref("new_password")], "Passwords do not match")
      .required("Confirm your new password"),
});

const PasswordLogin = () => {
   const navigate = useNavigate();
   const [showPassword, setShowPassword] = useState(false);
   const [alertOpen, setAlertOpen] = useState(false);
   const [alertMessage, setAlertMessage] = useState("");
   // "login" -> "forgot-request" -> "forgot-verify" -> back to "login"
   const [mode, setMode] = useState("login");
   const [resetUsername, setResetUsername] = useState("");
   const [infoMessage, setInfoMessage] = useState("");

   const showError = (message) => {
      setAlertMessage(message);
      setAlertOpen(true);
   };

   const handleLogin = async (values) => {
      const response = await AuthActions.MERCHANT_PASSWORD_LOGIN({
         username: values.username,
         password: values.password,
      });
      if (response?.status === 200) {
         const resData = await response.json();
         localStorage.setItem("savedT", resData.merchant_jwt);
         localStorage.setItem("waiter_mode", false);
         navigate("/orders");
      } else if (response?.status === 401) {
         showError("Invalid username or password");
      } else {
         showError("Something went wrong. Please try again.");
      }
   };

   const handleForgotRequest = async (values) => {
      await AuthActions.MERCHANT_FORGOT_PASSWORD_REQUEST({ username: values.username });
      // The backend always answers the same way whether or not the username
      // exists, so this screen must too -- it must never confirm or deny a
      // username by how it reacts.
      setResetUsername(values.username);
      setInfoMessage("If that username exists, a reset code has been sent.");
      setMode("forgot-verify");
   };

   const handleForgotVerify = async (values) => {
      const response = await AuthActions.MERCHANT_FORGOT_PASSWORD_VERIFY({
         username: resetUsername,
         otp: parseInt(values.otp, 10),
         new_password: values.new_password,
      });
      if (response?.status === 200) {
         setInfoMessage("Password updated. Sign in with your new password.");
         setMode("login");
      } else {
         showError("That code is invalid or has expired.");
      }
   };

   return (
      <ThemeProvider theme={theme}>
         <Alert
            open={alertOpen}
            onClose={() => setAlertOpen(false)}
            handleClose={() => setAlertOpen(false)}
            message={alertMessage}
         />

         {mode === "login" && (
            <Formik
               initialValues={{ username: "", password: "" }}
               validationSchema={loginSchema}
               onSubmit={handleLogin}>
               {({ handleSubmit, isSubmitting }) => (
                  <Form
                     className="w-[70%]"
                     onSubmit={handleSubmit}>
                     {infoMessage && (
                        <p className="text-[#0F6656] text-[0.85rem] mb-2">{infoMessage}</p>
                     )}
                     <div className="flex flex-col gap-[0.65rem]">
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">Username</p>
                           <Field name="username">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    fullWidth
                                    variant="outlined"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    placeholder="Enter username"
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="username"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">Password</p>
                           <Field name="password">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter password"
                                    InputProps={{
                                       endAdornment: (
                                          <InputAdornment position="end">
                                             <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword((prev) => !prev)}
                                                edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                             </IconButton>
                                          </InputAdornment>
                                       ),
                                    }}
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="password"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <button
                           type="submit"
                           disabled={isSubmitting}
                           className="bg-[#0F6656] text-[0.95rem] p-[0.55rem] rounded-lg text-[#fff] font-medium">
                           Sign in
                        </button>
                        <div
                           onClick={() => {
                              setInfoMessage("");
                              setMode("forgot-request");
                           }}
                           className="text-[#0F6656] text-[0.85rem] font-medium cursor-pointer self-start">
                           Forgot password?
                        </div>
                     </div>
                  </Form>
               )}
            </Formik>
         )}

         {mode === "forgot-request" && (
            <Formik
               initialValues={{ username: "" }}
               validationSchema={forgotRequestSchema}
               onSubmit={handleForgotRequest}>
               {({ handleSubmit, isSubmitting }) => (
                  <Form
                     className="w-[70%]"
                     onSubmit={handleSubmit}>
                     <p className="text-[0.85rem] mb-3 text-[#868B8C]">
                        Enter your username and we'll send a reset code to the phone or email on
                        file.
                     </p>
                     <div className="flex flex-col gap-[0.65rem]">
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">Username</p>
                           <Field name="username">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    fullWidth
                                    variant="outlined"
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    placeholder="Enter username"
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="username"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <button
                           type="submit"
                           disabled={isSubmitting}
                           className="bg-[#0F6656] text-[0.95rem] p-[0.55rem] rounded-lg text-[#fff] font-medium">
                           Send reset code
                        </button>
                        <div
                           onClick={() => setMode("login")}
                           className="text-[#0F6656] text-[0.85rem] font-medium cursor-pointer self-start">
                           Back to sign in
                        </div>
                     </div>
                  </Form>
               )}
            </Formik>
         )}

         {mode === "forgot-verify" && (
            <Formik
               initialValues={{ otp: "", new_password: "", confirm_password: "" }}
               validationSchema={forgotVerifySchema}
               onSubmit={handleForgotVerify}>
               {({ handleSubmit, isSubmitting }) => (
                  <Form
                     className="w-[70%]"
                     onSubmit={handleSubmit}>
                     {infoMessage && (
                        <p className="text-[#0F6656] text-[0.85rem] mb-2">{infoMessage}</p>
                     )}
                     <div className="flex flex-col gap-[0.65rem]">
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">Reset code</p>
                           <Field name="otp">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    fullWidth
                                    variant="outlined"
                                    inputMode="numeric"
                                    placeholder="4-digit code"
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="otp"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">New password</p>
                           <Field name="new_password">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="At least 8 characters"
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="new_password"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <div>
                           <p className="text-[0.9rem] mb-1 text-[#393D3D] font-medium">Confirm password</p>
                           <Field name="confirm_password">
                              {({ field }) => (
                                 <TextField
                                    size="small"
                                    {...field}
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Re-enter new password"
                                 />
                              )}
                           </Field>
                           <ErrorMessage
                              name="confirm_password"
                              component="div"
                              className="text-red-500 text-[0.8rem]"
                           />
                        </div>
                        <button
                           type="submit"
                           disabled={isSubmitting}
                           className="bg-[#0F6656] text-[0.95rem] p-[0.55rem] rounded-lg text-[#fff] font-medium">
                           Reset password
                        </button>
                        <div
                           onClick={() => {
                              setInfoMessage("");
                              setMode("login");
                           }}
                           className="text-[#0F6656] text-[0.85rem] font-medium cursor-pointer self-start">
                           Back to sign in
                        </div>
                     </div>
                  </Form>
               )}
            </Formik>
         )}
      </ThemeProvider>
   );
};

export default PasswordLogin;
